import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Users, FileText, DollarSign, ScrollText,
  Settings, LifeBuoy, LogOut, ShieldCheck, UserCog, GraduationCap, Building2,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/disputes", label: "Disputes & Mail", icon: FileText },
  { to: "/admin/billing", label: "Billing", icon: DollarSign },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
  { to: "/admin/team", label: "Team", icon: UserCog },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/training", label: "Training", icon: GraduationCap },
  { to: "/admin/help", label: "Help Center", icon: LifeBuoy },
];

export default function StaffLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState({ company_name: "CredVantage Pro", tagline: "" });

  useEffect(() => {
    api.get("/settings").then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <aside className="w-64 shrink-0 bg-slate-950 text-slate-100 flex flex-col border-r border-slate-800 fixed inset-y-0 z-30">
        <div className="px-5 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2">
            {brand.logo_url
              ? <img src={brand.logo_url} alt="logo" className="h-8 w-8 rounded object-cover" />
              : <ShieldCheck className="h-7 w-7 text-sky-400" />}
            <div>
              <div className="font-heading font-bold text-lg leading-tight text-white" data-testid="staff-brand-name">{brand.company_name}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">Staff OS</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {(user?.role === "platform_owner"
            ? [...NAV.slice(0, 6), { to: "/admin/organizations", label: "Organizations", icon: Building2 }, ...NAV.slice(6)]
            : NAV
          ).map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-sky-600/20 text-sky-300 border border-sky-700/40" : "text-slate-400 hover:bg-slate-800 hover:text-white border border-transparent"
                }`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-slate-800">
          <div className="text-sm font-medium text-slate-200">{user?.name}</div>
          <div className="text-xs text-slate-500 capitalize mb-2">{user?.role?.replace("_", " ")}</div>
          <button onClick={doLogout} data-testid="staff-logout-btn"
            className="flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors duration-150">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-w-0">
        <div className="px-6 lg:px-8 py-6 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
      <Toaster richColors position="top-right" />
    </div>
  );
}
